import { useEffect, useState } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  Link as RouterLink,
  matchPath,
} from "react-router-dom";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";

import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import InventoryIcon from "@mui/icons-material/Inventory";
import TuneIcon from "@mui/icons-material/Tune";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GroupIcon from "@mui/icons-material/Group";
import StraightenIcon from "@mui/icons-material/Straighten";
import CategoryIcon from "@mui/icons-material/Category";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import InsightsIcon from "@mui/icons-material/Insights";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LockClockIcon from "@mui/icons-material/LockClock";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useQuery } from "@tanstack/react-query";
import { branchesApi } from "@/api/endpoints";
import { MenuItem as MuiMenuItem, TextField } from "@mui/material";

import { useAuth } from "@/store/auth";
import { useFeatureFlags } from "@/store/featureFlags";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import type { TaxRegime } from "@/store/auth";

const SIDEBAR_OPEN = 272;
const SIDEBAR_RAIL = 72;
const SIDEBAR_STATE_KEY = "im_sidebar_state"; // "open" | "rail" | "hidden"

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  /**
   * Feature flag key required to show this item. Items without a `feature` are part of
   * the MVP 1 baseline and always show (subject to `permission`). Items with a `feature`
   * are hidden until an admin enables the flag at `/admin/feature-flags`.
   */
  feature?: string;
  /**
   * Tax regime gate. Items tagged here only render when the current tenant's regime
   * matches — e.g. GST e-invoice + e-way bill are India-specific so they're hidden
   * for tenants on `Vat` or `None`. Independent of feature flags: a VAT tenant might
   * have the `integration.gst.einvoice` flag mistakenly enabled, but the regime gate
   * still hides the menu entry because the integration would never produce results.
   */
  regimeOnly?: TaxRegime;
}

interface NavGroup {
  /** Stable id used by collapse state — does not appear in the UI. */
  key: string;
  /** Section title displayed as a subtle uppercase header in the sidebar. */
  title: string;
  items: NavItem[];
}

/**
 * Sidebar navigation organised by functional area. Phase metadata (MVP 1 / MVP 2 / Final)
 * is intentionally NOT surfaced here — it lives on each item's `feature` flag and is
 * enforced via the database-backed feature flag service. The admin toggles flags at
 * `/admin/feature-flags`; items materialise inside their functional group when their
 * flag flips on.
 *
 * Always-on items omit `feature`. MVP 2 / Final items declare the flag they belong to.
 */
const NAV: NavGroup[] = [
  {
    key: "main",
    title: "",
    items: [
      { label: "Dashboard",        path: "/dashboard",                       icon: <DashboardIcon /> },
    ],
  },
  {
    key: "catalog",
    title: "Catalog",
    items: [
      { label: "Items",            path: "/catalog/items",                   icon: <LocalPharmacyIcon />, permission: "inventory.item.view" },
      { label: "Vendors",          path: "/catalog/vendors",                 icon: <StoreIcon />,         permission: "procurement.vendor.view" },
      { label: "Customers",        path: "/catalog/customers",               icon: <GroupIcon />,         permission: "sales.customer.view" },
      { label: "Units of Measure", path: "/catalog/uoms",                    icon: <StraightenIcon />,    permission: "catalog.uom.view" },
      { label: "Item Categories",  path: "/catalog/item-categories",         icon: <CategoryIcon />,      permission: "catalog.category.view" },
      { label: "Item Forms",       path: "/catalog/item-forms",              icon: <VaccinesIcon />,      permission: "catalog.form.view" },
      { label: "Services",         path: "/catalog/services",                icon: <MiscellaneousServicesIcon />, permission: "sales.service.view" },
    ],
  },
  {
    key: "inventory",
    title: "Inventory",
    items: [
      { label: "Stock",            path: "/inventory/stock",                 icon: <InventoryIcon />,      permission: "inventory.stock.view" },
      { label: "Adjustments",      path: "/inventory/adjustments",           icon: <TuneIcon />,           permission: "inventory.adjustment.view" },
      { label: "Write-offs",       path: "/inventory/writeoffs",             icon: <DeleteSweepIcon />,    permission: "inventory.writeoff.view" },
      { label: "Internal transfers",path: "/inventory/internal-transfers",   icon: <CompareArrowsIcon />,  permission: "inventory.transfer.internal" },
      { label: "Stock takes",      path: "/inventory/stocktakes",            icon: <FactCheckIcon />,      permission: "inventory.stocktake.view" },
      { label: "Near expiry",      path: "/inventory/near-expiry",           icon: <EventBusyIcon />,      permission: "inventory.stock.view" },
      { label: "Predictive reorder", path: "/inventory/predictive-reorder",  icon: <InsightsIcon />,       permission: "inventory.stock.view",   feature: "inventory.predictive_reorder" },
      { label: "Narcotic register", path: "/inventory/narcotic-register",    icon: <HistoryEduIcon />,     permission: "clinical.narcotic.view", feature: "clinical.narcotic_register" },
    ],
  },
  {
    key: "procurement",
    title: "Procurement",
    items: [
      { label: "Purchase Orders",  path: "/procurement/purchase-orders",     icon: <LocalShippingIcon />,        permission: "procurement.po.view" },
      { label: "Reorder queue",    path: "/procurement/reorder-queue",       icon: <ShoppingCartCheckoutIcon />, permission: "procurement.po.create" },
      { label: "Goods Receipts",   path: "/procurement/grns",                icon: <MoveToInboxIcon />,          permission: "procurement.grn.view" },
      { label: "Purchase Invoices",path: "/procurement/purchase-invoices",   icon: <RequestQuoteIcon />,         permission: "procurement.pi.view" },
      { label: "Direct PI (bill entry)", path: "/procurement/purchase-invoices/direct", icon: <RequestQuoteIcon />, permission: "procurement.pi.create-direct", feature: "procurement.direct-pi" },
      { label: "Quotations",       path: "/procurement/quotations",          icon: <RequestQuoteIcon />,         permission: "procurement.quotation.view",   feature: "procurement.quotation" },
      { label: "Purchase Indents", path: "/procurement/purchase-requisitions", icon: <AssignmentIcon />, permission: "procurement.requisition.create", feature: "procurement.requisition" },
    ],
  },
  {
    key: "sales",
    title: "Sales",
    items: [
      { label: "OP Billing",       path: "/sales/invoices",                  icon: <ReceiptIcon />, permission: "sales.invoice.view" },
      { label: "IP Billing",       path: "/sales/ip-billing",                icon: <ReceiptIcon />, permission: "sales.invoice.view", feature: "sales.ip_billing" },
    ],
  },
  {
    key: "warehousing",
    title: "Warehousing",
    items: [
      { label: "Transfers",        path: "/warehouse/transfers",                   icon: <SwapHorizIcon />,  permission: "warehouse.transfer.view", feature: "transfer.inter_branch" },
      { label: "Transfer requisitions", path: "/warehouse/transfer-requisitions",  icon: <AssignmentIcon />, permission: "warehouse.transfer.view", feature: "transfer.requisition" },
    ],
  },
  {
    key: "logistics",
    title: "Logistics",
    items: [
      { label: "Shipments",        path: "/transport/shipments",             icon: <LocalShippingIcon />, permission: "transport.shipment.view",  feature: "transport.shipment" },
      { label: "Carriers",         path: "/transport/carriers",              icon: <LocalShippingIcon />, permission: "transport.carrier.manage", feature: "transport.shipment" },
      { label: "Vehicles",         path: "/transport/vehicles",              icon: <LocalShippingIcon />, permission: "transport.carrier.manage", feature: "transport.shipment" },
      { label: "Drivers",          path: "/transport/drivers",               icon: <PersonIcon />,        permission: "transport.carrier.manage", feature: "transport.shipment" },
    ],
  },
  {
    key: "pricing",
    title: "Pricing",
    items: [
      { label: "Price lists",      path: "/pricing/price-lists",             icon: <RequestQuoteIcon />, permission: "pricing.list.view", feature: "pricing.price_list" },
    ],
  },
  {
    key: "accounting",
    title: "Accounting",
    items: [
      { label: "Journals",          path: "/accounting/journals/new",        icon: <EditNoteIcon />,   permission: "accounting.journal.create" },
      { label: "Chart of accounts", path: "/accounting/chart-of-accounts",   icon: <AssignmentIcon />, permission: "accounting.account.view" },
      { label: "Periods",           path: "/accounting/periods",             icon: <LockClockIcon />,  permission: "accounting.journal.view" },
    ],
  },
  {
    key: "hospital",
    title: "Hospital",
    items: [
      { label: "Ward Issue",       path: "/hospital/ward",                   icon: <LocalHospitalIcon />, permission: "hospital.ward.issue",  feature: "hospital.ward" },
      { label: "OT Consumption",   path: "/hospital/ot",                     icon: <LocalHospitalIcon />, permission: "hospital.ot.issue",    feature: "hospital.ward" },
      { label: "CSSD Trays",       path: "/hospital/cssd",                   icon: <LocalHospitalIcon />, permission: "hospital.cssd.manage", feature: "hospital.ward" },
    ],
  },
  {
    key: "integrations",
    title: "Integrations",
    items: [
      { label: "HealNTrack",       path: "/integration/healntrack",          icon: <SyncAltIcon />,       permission: "integration.healntrack.sync",       feature: "integration.healntrack" },
      { label: "GST e-invoices",   path: "/integration/einvoices",           icon: <ReceiptIcon />,       permission: "integration.gst.einvoice.generate", feature: "integration.gst.einvoice", regimeOnly: "Gst" },
      { label: "E-Way bills",      path: "/integration/ewaybills",           icon: <LocalShippingIcon />, permission: "integration.gst.einvoice.generate", feature: "integration.eway_bill",   regimeOnly: "Gst" },
    ],
  },
  {
    key: "reports",
    title: "Reports",
    items: [
      { label: "Reports hub",      path: "/reports",                         icon: <AssessmentIcon /> ,  permission: "reports.stock.view" },
      { label: "Audit log",        path: "/admin/audit",                     icon: <HistoryEduIcon />,   permission: "audit.log.view" },
    ],
  },
  {
    key: "admin",
    title: "Administration",
    items: [
      { label: "Users",            path: "/admin/users",                     icon: <PersonIcon />,       permission: "admin.user.view" },
      { label: "Roles",            path: "/admin/roles",                     icon: <SecurityIcon />,     permission: "admin.role.view" },
      { label: "Permissions",      path: "/admin/permissions",               icon: <SecurityIcon />,     permission: "admin.role.manage" },
      { label: "Branches",         path: "/admin/branches",                  icon: <StoreIcon />,        permission: "admin.branch.view" },
      { label: "Warehouses",       path: "/admin/warehouses",                icon: <WarehouseIcon />,    permission: "warehouse.warehouse.view" },
      { label: "Feature flags",    path: "/admin/feature-flags",             icon: <TuneIcon />,         permission: "system.feature_flag.manage" },
      { label: "Tenant settings",  path: "/admin/tenant-settings",           icon: <TuneIcon />,         permission: "system.feature_flag.manage" },
      { label: "Demo guide",       path: "/demo-guide",                      icon: <SlideshowIcon /> },
    ],
  },
];

function readSidebarState(): "open" | "rail" | "hidden" {
  const v = typeof window === "undefined" ? null : window.localStorage.getItem(SIDEBAR_STATE_KEY);
  return v === "rail" || v === "hidden" ? v : "open";
}

export function Layout() {
  const [sidebarState, setSidebarState] = useState<"open" | "rail" | "hidden">(readSidebarState());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const activeBranchId = useAuth((s) => s.activeBranchId);
  const setActiveBranch = useAuth((s) => s.setActiveBranch);
  const hasPermission = useAuth((s) => s.hasPermission);
  const clear = useAuth((s) => s.clear);
  const featureFlags = useFeatureFlags();
  const { regime } = useTaxRegime();

  // S7.5 multi-branch switcher: list of branches the user can see.
  const branches = useQuery({
    queryKey: ["branches-switcher"],
    queryFn: () => branchesApi.list(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STATE_KEY, sidebarState);
  }, [sidebarState]);

  const isHome = location.pathname === "/" || location.pathname === "/dashboard";

  const handleLogout = () => {
    clear();
    navigate("/login", { replace: true });
  };

  const cycleSidebar = () => {
    setSidebarState((s) => (s === "open" ? "rail" : s === "rail" ? "hidden" : "open"));
  };

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const drawerWidth = sidebarState === "open" ? SIDEBAR_OPEN : sidebarState === "rail" ? SIDEBAR_RAIL : 0;
  const showLabels = sidebarState === "open";

  const renderGroup = (group: NavGroup) => {
    const visibleItems = group.items.filter((i) =>
      (!i.permission || hasPermission(i.permission)) &&
      (!i.feature    || featureFlags.isEnabled(i.feature)) &&
      (!i.regimeOnly || i.regimeOnly === regime));
    if (visibleItems.length === 0) return null;
    const collapsed = collapsedGroups.has(group.key);
    const hasTitle  = group.title.length > 0;

    return (
      <Box key={group.key} sx={{ mt: hasTitle ? 1 : 0 }}>
        {showLabels && hasTitle && (
          <ListItemButton onClick={() => toggleGroup(group.key)} sx={{ py: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                sx: { opacity: 0.65 },
              }}
              primary={group.title}
            />
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </ListItemButton>
        )}
        <Collapse in={!collapsed || !showLabels} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {visibleItems.map((item) => {
              const selected = !!matchPath({ path: item.path, end: false }, location.pathname);
              return (
                <ListItem key={item.path} disablePadding>
                  <Tooltip title={showLabels ? "" : item.label} placement="right">
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={selected}
                      sx={{ minHeight: 48, justifyContent: showLabels ? "initial" : "center", px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: showLabels ? 2 : "auto", justifyContent: "center" }}>
                        {item.icon}
                      </ListItemIcon>
                      {showLabels && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        {showLabels && hasTitle && <Divider sx={{ my: 0.5 }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* TOPBAR */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "#1565c0",
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={cycleSidebar}
            aria-label="Toggle sidebar"
            sx={{ minWidth: 48, minHeight: 48 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Back + Home — always visible, mockup v12 rule */}
          <Tooltip title="Back">
            <span>
              <IconButton
                color="inherit"
                disabled={isHome}
                onClick={() => navigate(-1)}
                aria-label="Go back"
                sx={{ minWidth: 48, minHeight: 48 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Home">
            <IconButton
              color="inherit"
              onClick={() => navigate("/dashboard")}
              aria-label="Go to home"
              sx={{ minWidth: 48, minHeight: 48 }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
              HealInventory
            </Typography>
          </Box>

          {(branches.data?.length ?? 0) > 1 && (
            <TextField
              select size="small" variant="outlined"
              value={activeBranchId ?? ""}
              onChange={(e) => setActiveBranch(e.target.value || null)}
              sx={{
                minWidth: 180,
                "& .MuiInputBase-root": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
              }}
            >
              {branches.data?.map(b => (
                <MuiMenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MuiMenuItem>
              ))}
            </TextField>
          )}

          <Tooltip title="Alerts (coming in S5)">
            <IconButton color="inherit" sx={{ minWidth: 48, minHeight: 48 }}>
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {user && (
            <>
              <Tooltip title={`${user.fullName} · ${user.roleName}`}>
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  color="inherit"
                  sx={{ minWidth: 48, minHeight: 48 }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main" }}>
                    {user.fullName?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                <MenuItem disabled>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{user.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {user.roleName}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* SIDEBAR */}
      <Drawer
        variant="persistent"
        open={sidebarState !== "hidden"}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "#0d2440",
            color: "rgba(255,255,255,0.92)",
            "& .MuiListItemIcon-root":   { color: "rgba(255,255,255,0.78)" },
            "& .MuiListItemButton-root.Mui-selected": {
              bgcolor: "rgba(255,255,255,0.12)",
            },
            "& .MuiListItemButton-root:hover": {
              bgcolor: "rgba(255,255,255,0.06)",
            },
            "& .MuiDivider-root": { borderColor: "rgba(255,255,255,0.1)" },
          },
          transition: "width 0.2s",
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: "auto", flexGrow: 1, pb: 2 }}>
          {NAV.map(renderGroup)}
        </Box>
        {showLabels && (
          <Box sx={{ p: 1, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <ListItemButton onClick={() => setSidebarState("rail")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ChevronLeftIcon />
              </ListItemIcon>
              <ListItemText primary="Collapse" primaryTypographyProps={{ fontSize: 13 }} />
            </ListItemButton>
          </Box>
        )}
      </Drawer>

      {/* CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: "width 0.2s",
          minHeight: "100vh",
          bgcolor: "#f5f6f8",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
